import type { MedicalRecordInput } from "@/lib/validations/medical-record";
import i18n from "@/i18n";

export const TRANSCRIPTION_PROCESSING_TTL_MS = 5 * 60 * 1000;

/**
 * Formats a timestamp to Brazilian Portuguese format
 */
export const formatTimestamp = (value?: string | null): string | null => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(parsed);
};

/**
 * Serializes structured data for database storage
 */
export const serializeStructured = (values: Partial<MedicalRecordInput>) =>
    JSON.parse(
        JSON.stringify(values, (_key, val) => {
            if (val instanceof Date) {
                return val.toISOString();
            }
            return val;
        }),
    );

/**
 * Checks if detected language is Portuguese and returns warning if not
 */
export const ensurePortugueseLabel = (language?: string): string | null => {
    if (!language) return null;
    return language.toLowerCase().startsWith("pt") ? null : `Idioma detectado: ${language}`;
};

/**
 * Formats duration in seconds to MM:SS format
 */
export const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
        .toString()
        .padStart(2, "0");
    const s = Math.floor(seconds % 60)
        .toString()
        .padStart(2, "0");
    return `${minutes}:${s}`;
};

/**
 * Formats time for auto-save label
 */
export const formatAutoSaveTime = (date: Date | null): string | null => {
    if (!date) return null;
    return new Intl.DateTimeFormat("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
};

export const isTranscriptionProcessingExpired = (
    session: { status: string | null; processing_started_at?: string | null } | null | undefined,
): boolean => {
    if (!session || session.status !== "processing" || !session.processing_started_at) {
        return false;
    }

    const startedAt = new Date(session.processing_started_at).getTime();
    if (Number.isNaN(startedAt)) {
        return false;
    }

    return Date.now() - startedAt > TRANSCRIPTION_PROCESSING_TTL_MS;
};

export const getTranscriptionProcessingTimeoutMessage = (): string =>
    i18n.t("medicalRecords:phase8.transcription.errors.processingTimeout");

